import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { Shield, Mail, Lock, Loader2, Eye, EyeOff } from 'lucide-react';

export default function StaffLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const { login, getAdminDashboardUrl } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login({ 
        email, 
        password, 
        totpCode: requires2FA ? totpCode : null 
      });
      
      if (result?.requires_2fa) {
        setRequires2FA(true);
        toast.info('Please enter your 2FA code');
        setLoading(false);
        return;
      }
      
      // Verify this is an admin account
      if (result?.role !== 'admin') {
        toast.error('This login is for staff members only');
        setLoading(false);
        return;
      }
      
      // If 2FA is not enabled, redirect to setup (MANDATORY for all admins)
      if (!result?.two_fa_enabled) {
        toast.info('Two-Factor Authentication is required for admin accounts');
        navigate('/staff/setup-2fa', { replace: true });
        return;
      }
      
      toast.success('Welcome back!');
      
      // Redirect to appropriate admin dashboard
      const adminDashboard = getAdminDashboardUrl(result.admin_role);
      navigate(adminDashboard, { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout showFooter={false} showMobileNav={false}>
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 rounded-xl bg-red-600 flex items-center justify-center">
                <Shield className="w-7 h-7 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl">
              {requires2FA ? '2FA Verification' : 'Staff Portal'}
            </CardTitle>
            <CardDescription>
              {requires2FA 
                ? 'Enter the code from your authenticator app' 
                : 'Authorized personnel only'}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              {!requires2FA ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="pl-10 h-12 rounded-xl"
                        data-testid="staff-email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="pl-10 pr-12 h-12 rounded-xl"
                        data-testid="staff-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="totp">Authentication Code</Label>
                  <div className="relative">
                    <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="totp"
                      type="text"
                      placeholder="Enter code"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.toUpperCase().slice(0, 8))}
                      maxLength={8}
                      required
                      className="pl-10 h-12 rounded-xl text-center text-xl tracking-widest"
                      autoFocus
                      data-testid="staff-2fa-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground text-center">
                    Enter the 6-digit code from your authenticator app or an 8-character backup code
                  </p>
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full h-12 rounded-xl bg-red-600 hover:bg-red-700"
                disabled={loading}
                data-testid="staff-login-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {requires2FA ? 'Verifying...' : 'Signing in...'}
                  </>
                ) : (
                  requires2FA ? 'Verify & Sign in' : 'Sign in'
                )}
              </Button>

              {requires2FA && (
                <Button 
                  type="button"
                  variant="ghost" 
                  className="w-full"
                  onClick={() => {
                    setRequires2FA(false);
                    setTotpCode('');
                  }}
                >
                  Back to login
                </Button>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
